/**
 * KPI ENGINE — safe configurable formula evaluation, scoring and anti-fraud.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * KPI points become money. A wrong number here is a wrong payslip, so the
 * engine obeys five hard rules:
 *
 *   1. PURE + DETERMINISTIC. No I/O, no clock reads, no randomness. The caller
 *      aggregates `task_events` and passes the numbers in. The same inputs must
 *      produce byte-identical output forever, so an old payslip can be
 *      recomputed and defended in an appeal.
 *   2. INTEGER-ONLY. Every stored value is an integer in MILLI units (1 point =
 *      1000 milli-points, 1 task = 1000 milli-units). Ratios are basis points.
 *      Division goes through `divRoundHalfUp` so rounding is defined, not
 *      whatever the FPU felt like.
 *   3. NO CODE-LEVEL FORMULAS. Every coefficient comes from a `kpi_rules` row.
 *      `DEFAULT_KPI_RULES` below is SEED DATA, not logic.
 *   4. NO `eval`, NO `new Function`. Formulas are expression TREES walked by
 *      the whitelisted interpreter in `evaluateExpression`. A tenant admin can
 *      edit a formula from the Admin app; that must never become remote code
 *      execution inside a Cloudflare Worker.
 *   5. NO LLM IN THE PATH. A model may translate or summarise an explanation
 *      that this file produced. It may never decide points, pay or discipline.
 */

import { AppError } from '@sonauto/utilities/errors';
import { BP_SCALE, divRoundHalfUp } from '@sonauto/utilities/money';
import type { Millis } from '@sonauto/utilities/time';

/** Bumped when the scoring maths changes; stored alongside every score. */
export const KPI_ENGINE_VERSION = '1';

/** 1 point = 1000 milli-points; 1 unit = 1000 milli-units. */
export const MILLI = 1000;

// ---------------------------------------------------------------------------
// Enums mirrored from migration 0008
// ---------------------------------------------------------------------------

/** `kpi_rules.metric`. */
export type KpiMetric =
  | 'TASK_COUNT'
  | 'ON_TIME_RATE'
  | 'COMPLETION_RATE'
  | 'AVG_RESPONSE_SECONDS'
  | 'AVG_DURATION_SECONDS'
  | 'RETURN_RATE'
  | 'REMAKE_RATE'
  | 'CUSTOMER_RATING'
  | 'TABLES_SERVED'
  | 'ITEMS_SERVED'
  | 'SUPPORT_COUNT'
  | 'SELF_CLAIM_RATE'
  | 'REJECT_RATE'
  | 'ABANDON_RATE'
  | 'LATE_MINUTES'
  | 'EARLY_LEAVE_MINUTES'
  | 'WORKED_HOURS'
  | 'REVENUE_PER_HOUR'
  | 'DIFFICULTY_WEIGHTED_LOAD';

/** `kpi_rules.aggregation` — how the CALLER aggregated raw events. Carried
 *  through to `kpi_score_details` so the number can be reproduced. */
export type KpiAggregation = 'SUM' | 'AVG' | 'COUNT' | 'RATIO' | 'MIN' | 'MAX' | 'PERCENTILE';

/** `kpi_rules.normalization`. */
export type KpiNormalization = 'NONE' | 'PER_HOUR' | 'PER_SHIFT_BUSYNESS' | 'PER_BRANCH_AVERAGE' | 'Z_SCORE' | 'PERCENT_OF_TARGET';

/** `task_events.event_type`. */
export type TaskEventType =
  | 'CREATED'
  | 'OFFERED'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'STARTED'
  | 'PAUSED'
  | 'RESUMED'
  | 'COMPLETED'
  | 'FAILED'
  | 'REASSIGNED'
  | 'ESCALATED'
  | 'CANCELLED'
  | 'EXPIRED'
  | 'EVIDENCE_ADDED'
  | 'QR_SCANNED';

/** Which `kpi_scores` bucket a rule's points land in. Not a DB column: it is
 *  how a rule maps onto base/bonus/penalty, so it lives on the rule config. */
export type KpiContribution = 'BASE' | 'BONUS' | 'PENALTY';

/** Traffic-light band from `kpi_rules.threshold_json`. */
export type KpiBand = 'GREEN' | 'AMBER' | 'RED' | 'NONE';

// ---------------------------------------------------------------------------
// Metric values
// ---------------------------------------------------------------------------

/**
 * An aggregated metric.
 *
 * `raw` is ALWAYS an integer in MILLI units of the metric's natural unit:
 *   TASK_COUNT        42 tasks        -> 42000
 *   ON_TIME_RATE      95%             -> 950      (rates: 1000 = 100%)
 *   AVG_DURATION_SEC  212.5 seconds   -> 212500
 *   CUSTOMER_RATING   4.6 stars       -> 4600
 *
 * `sampleCount` is the number of underlying `task_events` rows. A rule scored
 * from three events is statistically meaningless; the caller can suppress or
 * flag it, and it is written to `kpi_score_details.sample_count` either way.
 */
export interface MetricValue {
  readonly raw: number;
  readonly sampleCount: number;
}

// ---------------------------------------------------------------------------
// Expression trees
// ---------------------------------------------------------------------------

export type ExprArithOp = '+' | '-' | '*' | '/' | 'min' | 'max' | 'clamp';
export type ExprCmpOp = '>' | '>=' | '<' | '<=' | '==' | '!=';

/** Literal integer. Non-integers are rejected: floats never enter scoring. */
export interface ExprConst {
  readonly k: 'const';
  readonly v: number;
}
/** Named variable, resolved from the `vars` bag supplied by `scoreRule`. */
export interface ExprVar {
  readonly k: 'var';
  readonly name: string;
}
/** Arithmetic. `-` with one argument negates; `clamp` takes (value, lo, hi). */
export interface ExprOp {
  readonly k: 'op';
  readonly op: ExprArithOp;
  readonly args: readonly ExprNode[];
}
/** Comparison, yielding 1 or 0 — booleans are integers here too. */
export interface ExprCmp {
  readonly k: 'cmp';
  readonly op: ExprCmpOp;
  readonly left: ExprNode;
  readonly right: ExprNode;
}
/** Branch. `cond` is truthy when it evaluates to a non-zero integer. */
export interface ExprIf {
  readonly k: 'if';
  readonly cond: ExprNode;
  readonly then: ExprNode;
  readonly else: ExprNode;
}

export type ExprNode = ExprConst | ExprVar | ExprOp | ExprCmp | ExprIf;

/**
 * Maximum nesting depth. A denial-of-service guard: formula JSON is
 * tenant-editable and is evaluated inside a Worker with a CPU budget, so a
 * 10,000-deep tree must be rejected rather than blow the stack.
 */
export const MAX_EXPRESSION_DEPTH = 32;

export type KpiExpressionErrorReason =
  | 'UNKNOWN_NODE'
  | 'UNKNOWN_VARIABLE'
  | 'DIVISION_BY_ZERO'
  | 'DEPTH_EXCEEDED'
  | 'BAD_ARITY'
  | 'NON_INTEGER'
  | 'UNSAFE_INTEGER';

/**
 * Typed failure from the interpreter. Extends AppError so the Worker layer
 * turns it into a 400 with a stable machine code, and so a broken formula
 * surfaces as "your rule is invalid" instead of a 500.
 */
export class KpiExpressionError extends AppError {
  readonly reason: KpiExpressionErrorReason;

  constructor(reason: KpiExpressionErrorReason, message: string, details?: Record<string, unknown>) {
    super('VALIDATION_FAILED', message, { details: { reason, ...details }, messageCode: `KPI_EXPR_${reason}` });
    this.name = 'KpiExpressionError';
    this.reason = reason;
  }
}

/**
 * SAFE expression-tree interpreter.
 *
 * WHY not `eval`/`new Function`: formulas are stored in `kpi_rules.formula_json`
 * and are editable by tenant admins. Interpreting a whitelisted node set means
 * the worst a malicious formula can do is return a wrong number — which the
 * clamps, the audit log and the appeals flow already handle.
 *
 * All arithmetic is integer. Division rounds half-away-from-zero via
 * `divRoundHalfUp` so `7/2 = 4` and `-7/2 = -4`, deterministically, on every
 * platform.
 *
 * @throws KpiExpressionError for unknown nodes, unknown variables, division by
 *         zero, excessive depth, wrong arity, or non-integer values.
 */
export function evaluateExpression(node: ExprNode, vars: Readonly<Record<string, number>>): number {
  return evalNode(node, vars, 0);
}

function evalNode(node: ExprNode, vars: Readonly<Record<string, number>>, depth: number): number {
  if (depth > MAX_EXPRESSION_DEPTH) {
    throw new KpiExpressionError('DEPTH_EXCEEDED', `Công thức KPI lồng quá sâu (> ${MAX_EXPRESSION_DEPTH} cấp).`, {
      depth,
    });
  }

  switch (node.k) {
    case 'const':
      return assertInteger(node.v, 'const');

    case 'var': {
      if (!Object.prototype.hasOwnProperty.call(vars, node.name)) {
        throw new KpiExpressionError('UNKNOWN_VARIABLE', `Biến không hợp lệ trong công thức KPI: ${node.name}.`, {
          name: node.name,
        });
      }
      const v = vars[node.name];
      if (v === undefined) {
        throw new KpiExpressionError('UNKNOWN_VARIABLE', `Biến không hợp lệ trong công thức KPI: ${node.name}.`, {
          name: node.name,
        });
      }
      return assertInteger(v, `var:${node.name}`);
    }

    case 'cmp': {
      const l = evalNode(node.left, vars, depth + 1);
      const r = evalNode(node.right, vars, depth + 1);
      return compare(node.op, l, r) ? 1 : 0;
    }

    case 'if': {
      const cond = evalNode(node.cond, vars, depth + 1);
      return cond !== 0 ? evalNode(node.then, vars, depth + 1) : evalNode(node.else, vars, depth + 1);
    }

    case 'op':
      return evalOp(node, vars, depth);

    default:
      // Defensive: the JSON came from the database and may predate this build.
      throw new KpiExpressionError('UNKNOWN_NODE', 'Nút công thức KPI không được hỗ trợ.', {
        node: (node as { k?: unknown }).k,
      });
  }
}

function evalOp(node: ExprOp, vars: Readonly<Record<string, number>>, depth: number): number {
  const args = node.args.map((a) => evalNode(a, vars, depth + 1));
  const first = args[0];

  switch (node.op) {
    case '+': {
      requireArity(node.op, args.length, 1);
      let acc = 0;
      for (const a of args) acc += a;
      return assertInteger(acc, 'op:+');
    }
    case '*': {
      requireArity(node.op, args.length, 1);
      let acc = 1;
      for (const a of args) acc *= a;
      return assertInteger(acc, 'op:*');
    }
    case '-': {
      requireArity(node.op, args.length, 1);
      if (first === undefined) throw arityError(node.op, args.length, 1);
      if (args.length === 1) return assertInteger(-first, 'op:-');
      let acc = first;
      for (let i = 1; i < args.length; i++) acc -= args[i] ?? 0;
      return assertInteger(acc, 'op:-');
    }
    case '/': {
      requireArity(node.op, args.length, 2);
      if (first === undefined) throw arityError(node.op, args.length, 2);
      let acc = first;
      for (let i = 1; i < args.length; i++) {
        const d = args[i] ?? 0;
        if (d === 0) {
          throw new KpiExpressionError('DIVISION_BY_ZERO', 'Công thức KPI chia cho 0.', { op: node.op });
        }
        acc = divRoundHalfUp(acc, d);
      }
      return acc;
    }
    case 'min': {
      requireArity(node.op, args.length, 1);
      if (first === undefined) throw arityError(node.op, args.length, 1);
      let acc = first;
      for (const a of args) if (a < acc) acc = a;
      return acc;
    }
    case 'max': {
      requireArity(node.op, args.length, 1);
      if (first === undefined) throw arityError(node.op, args.length, 1);
      let acc = first;
      for (const a of args) if (a > acc) acc = a;
      return acc;
    }
    case 'clamp': {
      if (args.length !== 3) throw arityError(node.op, args.length, 3);
      const [value, lo, hi] = args;
      if (value === undefined || lo === undefined || hi === undefined) throw arityError(node.op, args.length, 3);
      if (lo > hi) {
        throw new KpiExpressionError('BAD_ARITY', 'clamp: cận dưới lớn hơn cận trên.', { lo, hi });
      }
      return value < lo ? lo : value > hi ? hi : value;
    }
    default:
      throw new KpiExpressionError('UNKNOWN_NODE', 'Phép toán KPI không được hỗ trợ.', {
        op: (node as { op?: unknown }).op,
      });
  }
}

function compare(op: ExprCmpOp, l: number, r: number): boolean {
  switch (op) {
    case '>':
      return l > r;
    case '>=':
      return l >= r;
    case '<':
      return l < r;
    case '<=':
      return l <= r;
    case '==':
      return l === r;
    case '!=':
      return l !== r;
    default:
      throw new KpiExpressionError('UNKNOWN_NODE', 'Phép so sánh KPI không được hỗ trợ.', { op });
  }
}

function requireArity(op: string, actual: number, min: number): void {
  if (actual < min) throw arityError(op, actual, min);
}

function arityError(op: string, actual: number, expected: number): KpiExpressionError {
  return new KpiExpressionError('BAD_ARITY', `Phép toán ${op} cần ít nhất ${expected} tham số, nhận ${actual}.`, {
    op,
    actual,
    expected,
  });
}

function assertInteger(n: number, where: string): number {
  if (!Number.isInteger(n)) {
    throw new KpiExpressionError('NON_INTEGER', `Giá trị KPI phải là số nguyên (${where}).`, { where, value: n });
  }
  if (!Number.isSafeInteger(n)) {
    throw new KpiExpressionError('UNSAFE_INTEGER', `Giá trị KPI vượt ngưỡng số nguyên an toàn (${where}).`, { where });
  }
  return n;
}

// ---------------------------------------------------------------------------
// Rule configuration
// ---------------------------------------------------------------------------

/** One band of `kpi_rules.threshold_json`, e.g. `{ "gte": 9000 }`. */
export interface KpiThreshold {
  readonly gte?: number;
  readonly gt?: number;
  readonly lte?: number;
  readonly lt?: number;
}

/** Parsed `kpi_rules.threshold_json`. Values compare against the NORMALIZED value. */
export interface KpiThresholds {
  readonly green?: KpiThreshold;
  readonly amber?: KpiThreshold;
  readonly red?: KpiThreshold;
}

/**
 * A parsed `kpi_rules` row.
 *
 * `formula` (from `formula_json`) wins when present; otherwise the engine uses
 * the linear form `points = normalized * pointsPerUnitMilli / 1000`. Most rules
 * are linear — the tree exists for the ones that are not (tiered bonuses,
 * "only pay above target", penalties that cap out).
 */
export interface KpiRuleConfig {
  readonly id: string;
  readonly code: string;
  readonly nameVi: string;
  readonly metric: KpiMetric;
  readonly aggregation: KpiAggregation;
  readonly formula?: ExprNode | null;
  /** Contribution weight in basis points; 10000 = full weight. */
  readonly weightBp: number;
  /** Milli-points per one (milli) unit of the normalized metric. May be negative. */
  readonly pointsPerUnitMilli: number;
  /** Floor applied AFTER weighting. Null = unbounded. */
  readonly minPointsMilli: number | null;
  /** Ceiling applied AFTER weighting. Null = unbounded. Stops runaway payouts. */
  readonly maxPointsMilli: number | null;
  /** Target in milli-units, used by PERCENT_OF_TARGET and the explanation. */
  readonly targetValueMilli: number | null;
  readonly normalization: KpiNormalization;
  readonly thresholds: KpiThresholds;
  readonly version: number;
  /** Which `kpi_scores` bucket this rule's points land in. Default BASE. */
  readonly contribution?: KpiContribution;
  readonly roleCode?: string | null;
  readonly taskType?: string | null;
  /** Names the deterministic aggregate query, for `kpi_score_details`. */
  readonly sourceQueryRef?: string | null;
}

// ---------------------------------------------------------------------------
// Normalization
// ---------------------------------------------------------------------------

/**
 * Everything needed to make two employees comparable.
 *
 * `busynessFactorBp` is the branch's demand during the period relative to its
 * own baseline (10000 = a typical shift, 20000 = twice as busy).
 */
export interface NormalizationContext {
  /** Minutes actually worked in the period (from attendance, server clock). */
  readonly workedMinutes: number;
  /** `kpi_scores.busyness_factor_bp`. */
  readonly busynessFactorBp: number;
  /** Branch mean of this metric for the period, milli-units. */
  readonly branchAverageMilli?: number | null;
  /** Branch standard deviation, milli-units, for Z_SCORE. */
  readonly branchStdDevMilli?: number | null;
  /** Overrides the rule's target when the caller has a period-specific one. */
  readonly targetValueMilli?: number | null;
}

/**
 * Make a raw metric comparable across shifts, branches and shift lengths.
 *
 * THE INTENT — read this before changing anything here:
 * A server on a dead Tuesday lunch physically cannot complete as many tasks as
 * one on a Saturday night. Scoring raw counts would mean the roster, not the
 * effort, decides pay, and staff would fight over shifts instead of working
 * them. Normalization removes the part of the number that the employee did not
 * control:
 *
 *   NONE               raw as-is (already comparable, e.g. a rate)
 *   PER_HOUR           raw scaled to a 60-minute hour — removes shift LENGTH
 *   PER_SHIFT_BUSYNESS raw divided by the shift's busyness — removes DEMAND
 *   PER_BRANCH_AVERAGE raw as basis points of the branch mean (10000 = average)
 *                      — removes BRANCH size/format differences
 *   Z_SCORE            (raw - mean) in MILLI-SIGMA, integer-approximated —
 *                      rank-like, robust to a whole branch having a slow day
 *   PERCENT_OF_TARGET  raw as basis points of target (10000 = exactly on target)
 *
 * Modes that return a RATIO (PER_BRANCH_AVERAGE, PERCENT_OF_TARGET) yield basis
 * points, so `pointsPerUnitMilli` for such a rule is read as "milli-points per
 * milli-unit of ratio". Z_SCORE yields milli-sigma. Documented on each rule.
 *
 * Degenerate inputs (zero worked minutes, zero branch average, zero deviation)
 * NEVER throw and NEVER divide by zero: they fall back to the raw value or 0,
 * because a payroll job must not crash on one employee with a broken timesheet.
 */
export function normalizeMetric(value: number, normalization: KpiNormalization, context: NormalizationContext): number {
  const raw = Math.trunc(value);

  switch (normalization) {
    case 'NONE':
      return raw;

    case 'PER_HOUR': {
      const minutes = Math.trunc(context.workedMinutes);
      if (minutes <= 0) return raw; // no timesheet -> cannot rescale; caller flags it
      return divRoundHalfUp(raw * 60, minutes);
    }

    case 'PER_SHIFT_BUSYNESS': {
      const busy = Math.trunc(context.busynessFactorBp);
      if (busy <= 0) return raw;
      return divRoundHalfUp(raw * BP_SCALE, busy);
    }

    case 'PER_BRANCH_AVERAGE': {
      const avg = Math.trunc(context.branchAverageMilli ?? 0);
      if (avg <= 0) return raw;
      return divRoundHalfUp(raw * BP_SCALE, avg);
    }

    case 'Z_SCORE': {
      const avg = Math.trunc(context.branchAverageMilli ?? 0);
      const sd = Math.trunc(context.branchStdDevMilli ?? 0);
      if (sd <= 0) return 0; // everyone identical -> nobody is above or below
      return divRoundHalfUp((raw - avg) * MILLI, sd);
    }

    case 'PERCENT_OF_TARGET': {
      const target = Math.trunc(context.targetValueMilli ?? 0);
      if (target <= 0) return raw;
      return divRoundHalfUp(raw * BP_SCALE, target);
    }

    default:
      return raw;
  }
}

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

/** Everything `scoreRule` / `computeKpiScore` need, beyond the rule + metric. */
export interface KpiScoringContext extends NormalizationContext {
  readonly tenantId: string;
  readonly branchId: string;
  readonly employeeId: string;
  readonly periodType: 'SHIFT' | 'DAY' | 'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR' | 'CUSTOM';
  readonly periodKey: string;
  readonly periodStart: Millis;
  readonly periodEnd: Millis;
  /** Server clock, injected — the engine never reads a clock itself. */
  readonly computedAt: Millis;
  readonly shiftId?: string | null;
}

/** Reproducibility payload written to `kpi_score_details.evidence_json`. */
export interface KpiEvidence extends Record<string, unknown> {
  ruleCode: string;
  ruleVersion: number;
  metric: KpiMetric;
  aggregation: KpiAggregation;
  normalization: KpiNormalization;
  rawValueMilli: number;
  normalizedValueMilli: number;
  targetValueMilli: number | null;
  weightBp: number;
  sampleCount: number;
  pointsBeforeWeightMilli: number;
  pointsBeforeClampMilli: number;
  pointsMilli: number;
  band: KpiBand;
  busynessFactorBp: number;
  workedMinutes: number;
  engineVersion: string;
  usedFormula: boolean;
}

export interface RuleScore {
  readonly pointsMilli: number;
  /** A sentence the employee can read in the Staff App and dispute if wrong. */
  readonly explanationVi: string;
  readonly evidence: KpiEvidence;
}

/**
 * Score ONE rule against ONE aggregated metric.
 *
 * Pipeline (fixed, documented, integer at every step):
 *   1. normalize        `normalizeMetric`
 *   2. points           formula tree if present, else linear
 *                       `normalized * pointsPerUnitMilli / 1000`
 *   3. weight           `points * weightBp / 10000`
 *   4. clamp            min/max — the last line of defence against a
 *                       mis-typed coefficient paying someone a fortune
 *
 * Clamping happens AFTER weighting on purpose: min/max_points_milli are the
 * rule's contribution limits as seen on the payslip, not intermediate bounds.
 */
export function scoreRule(rule: KpiRuleConfig, metric: MetricValue, context: KpiScoringContext): RuleScore {
  const raw = Math.trunc(metric.raw);
  const normContext: NormalizationContext = {
    workedMinutes: context.workedMinutes,
    busynessFactorBp: context.busynessFactorBp,
    branchAverageMilli: context.branchAverageMilli ?? null,
    branchStdDevMilli: context.branchStdDevMilli ?? null,
    targetValueMilli: context.targetValueMilli ?? rule.targetValueMilli,
  };
  const normalized = normalizeMetric(raw, rule.normalization, normContext);

  const formula = rule.formula ?? null;
  const usedFormula = formula !== null;
  const pointsBeforeWeight = usedFormula
    ? evaluateExpression(formula, buildVars(rule, metric, raw, normalized, context))
    : divRoundHalfUp(normalized * rule.pointsPerUnitMilli, MILLI);

  const weighted = divRoundHalfUp(pointsBeforeWeight * rule.weightBp, BP_SCALE);
  const pointsMilli = clampPoints(weighted, rule.minPointsMilli, rule.maxPointsMilli);
  const band = classify(normalized, rule.thresholds);

  const evidence: KpiEvidence = {
    ruleCode: rule.code,
    ruleVersion: rule.version,
    metric: rule.metric,
    aggregation: rule.aggregation,
    normalization: rule.normalization,
    rawValueMilli: raw,
    normalizedValueMilli: normalized,
    targetValueMilli: rule.targetValueMilli,
    weightBp: rule.weightBp,
    sampleCount: Math.max(0, Math.trunc(metric.sampleCount)),
    pointsBeforeWeightMilli: pointsBeforeWeight,
    pointsBeforeClampMilli: weighted,
    pointsMilli,
    band,
    busynessFactorBp: context.busynessFactorBp,
    workedMinutes: context.workedMinutes,
    engineVersion: KPI_ENGINE_VERSION,
    usedFormula,
  };

  return {
    pointsMilli,
    explanationVi: explainVi(rule, raw, pointsMilli, metric.sampleCount),
    evidence,
  };
}

/**
 * Variables visible to a formula tree. Deliberately a small, stable, snake_case
 * whitelist: a formula that references anything else fails loudly with
 * UNKNOWN_VARIABLE instead of silently scoring 0.
 */
function buildVars(
  rule: KpiRuleConfig,
  metric: MetricValue,
  raw: number,
  normalized: number,
  context: KpiScoringContext,
): Record<string, number> {
  return {
    raw,
    normalized,
    sample_count: Math.max(0, Math.trunc(metric.sampleCount)),
    target: Math.trunc(context.targetValueMilli ?? rule.targetValueMilli ?? 0),
    points_per_unit: rule.pointsPerUnitMilli,
    weight_bp: rule.weightBp,
    worked_minutes: Math.trunc(context.workedMinutes),
    busyness_bp: Math.trunc(context.busynessFactorBp),
    branch_average: Math.trunc(context.branchAverageMilli ?? 0),
    branch_stddev: Math.trunc(context.branchStdDevMilli ?? 0),
    milli: MILLI,
    bp_scale: BP_SCALE,
  };
}

function clampPoints(points: number, min: number | null, max: number | null): number {
  let p = points;
  if (min !== null && p < min) p = min;
  if (max !== null && p > max) p = max;
  return p;
}

/** Traffic light for the Admin dashboards. Green wins over amber over red. */
export function classify(normalizedValue: number, thresholds: KpiThresholds): KpiBand {
  if (matches(normalizedValue, thresholds.green)) return 'GREEN';
  if (matches(normalizedValue, thresholds.amber)) return 'AMBER';
  if (matches(normalizedValue, thresholds.red)) return 'RED';
  return 'NONE';
}

function matches(value: number, t: KpiThreshold | undefined): boolean {
  if (t === undefined) return false;
  let hasBound = false;
  if (t.gte !== undefined) {
    if (value < t.gte) return false;
    hasBound = true;
  }
  if (t.gt !== undefined) {
    if (value <= t.gt) return false;
    hasBound = true;
  }
  if (t.lte !== undefined) {
    if (value > t.lte) return false;
    hasBound = true;
  }
  if (t.lt !== undefined) {
    if (value >= t.lt) return false;
    hasBound = true;
  }
  return hasBound;
}

// ---------------------------------------------------------------------------
// Vietnamese explanations
// ---------------------------------------------------------------------------

interface MetricLabel {
  /** Sentence opener, e.g. 'Hoàn thành'. */
  readonly prefixVi: string;
  /** Unit noun, e.g. 'nhiệm vụ'. Empty when the value is a percentage. */
  readonly unitVi: string;
  /** Render the value as a percentage (raw 1000 milli = 100%). */
  readonly asPercent: boolean;
}

/**
 * Human phrasing per metric. WHY it is a table and not generated text: an
 * employee disputing a deduction must see the same wording every time, and the
 * i18n layer must be able to map it. No LLM writes this sentence.
 */
const METRIC_LABELS: Readonly<Record<KpiMetric, MetricLabel>> = {
  TASK_COUNT: { prefixVi: 'Hoàn thành', unitVi: 'nhiệm vụ', asPercent: false },
  ON_TIME_RATE: { prefixVi: 'Tỉ lệ đúng hạn', unitVi: '', asPercent: true },
  COMPLETION_RATE: { prefixVi: 'Tỉ lệ hoàn thành', unitVi: '', asPercent: true },
  AVG_RESPONSE_SECONDS: { prefixVi: 'Thời gian nhận việc trung bình', unitVi: 'giây', asPercent: false },
  AVG_DURATION_SECONDS: { prefixVi: 'Thời gian xử lý trung bình', unitVi: 'giây', asPercent: false },
  RETURN_RATE: { prefixVi: 'Tỉ lệ trả món', unitVi: '', asPercent: true },
  REMAKE_RATE: { prefixVi: 'Tỉ lệ làm lại món', unitVi: '', asPercent: true },
  CUSTOMER_RATING: { prefixVi: 'Điểm đánh giá của khách', unitVi: 'sao', asPercent: false },
  TABLES_SERVED: { prefixVi: 'Phục vụ', unitVi: 'bàn', asPercent: false },
  ITEMS_SERVED: { prefixVi: 'Xử lý', unitVi: 'món', asPercent: false },
  SUPPORT_COUNT: { prefixVi: 'Hỗ trợ đồng nghiệp', unitVi: 'lượt', asPercent: false },
  SELF_CLAIM_RATE: { prefixVi: 'Tỉ lệ chủ động nhận việc', unitVi: '', asPercent: true },
  REJECT_RATE: { prefixVi: 'Tỉ lệ từ chối nhiệm vụ', unitVi: '', asPercent: true },
  ABANDON_RATE: { prefixVi: 'Tỉ lệ bỏ dở nhiệm vụ', unitVi: '', asPercent: true },
  LATE_MINUTES: { prefixVi: 'Đi muộn', unitVi: 'phút', asPercent: false },
  EARLY_LEAVE_MINUTES: { prefixVi: 'Về sớm', unitVi: 'phút', asPercent: false },
  WORKED_HOURS: { prefixVi: 'Số giờ làm việc', unitVi: 'giờ', asPercent: false },
  REVENUE_PER_HOUR: { prefixVi: 'Doanh thu mỗi giờ', unitVi: '₫', asPercent: false },
  DIFFICULTY_WEIGHTED_LOAD: { prefixVi: 'Khối lượng công việc quy đổi', unitVi: 'điểm tải', asPercent: false },
};

/**
 * Render a milli-integer for humans, Vietnamese style (comma decimals, at most
 * three decimals, trailing zeros trimmed). Display only — never parsed back.
 * String building, not float maths, so 42000 renders "42" and never "41.999".
 */
export function formatMilliVi(milliValue: number): string {
  const v = Math.trunc(milliValue);
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  const whole = Math.floor(abs / MILLI);
  const frac = abs % MILLI;
  if (frac === 0) return `${sign}${whole}`;
  const fracStr = String(frac).padStart(3, '0').replace(/0+$/, '');
  return `${sign}${whole},${fracStr}`;
}

/** Signed milli-points, e.g. "+120" / "-15". */
function formatSignedPointsVi(pointsMilli: number): string {
  const s = formatMilliVi(pointsMilli);
  return pointsMilli > 0 ? `+${s}` : s;
}

/**
 * The sentence the Staff App shows verbatim, e.g.
 *   "Hoàn thành 42 nhiệm vụ (mục tiêu 35) → +120 điểm"
 *   "Tỉ lệ đúng hạn 95% (mục tiêu 90%) → +45 điểm"
 * With no data at all it says so plainly rather than implying a zero score.
 */
function explainVi(rule: KpiRuleConfig, rawMilli: number, pointsMilli: number, sampleCount: number): string {
  const label = METRIC_LABELS[rule.metric];
  const valueStr = label.asPercent ? `${formatMilliVi(rawMilli * 100)}%` : formatMilliVi(rawMilli);
  const unit = label.unitVi.length > 0 ? ` ${label.unitVi}` : '';

  if (sampleCount <= 0 && rawMilli === 0) {
    return `${rule.nameVi}: chưa có dữ liệu trong kỳ → ${formatSignedPointsVi(pointsMilli)} điểm`;
  }

  const target = rule.targetValueMilli;
  const targetStr =
    target === null
      ? ''
      : label.asPercent
        ? ` (mục tiêu ${formatMilliVi(target * 100)}%)`
        : ` (mục tiêu ${formatMilliVi(target)}${unit})`;

  return `${label.prefixVi} ${valueStr}${unit}${targetStr} → ${formatSignedPointsVi(pointsMilli)} điểm`;
}

// ---------------------------------------------------------------------------
// Aggregate scoring
// ---------------------------------------------------------------------------

/** One row of `kpi_score_details`, ready to insert. */
export interface KpiScoreDetail {
  readonly kpiRuleId: string;
  readonly ruleCode: string;
  readonly metric: KpiMetric;
  readonly rawValueMilli: number;
  readonly normalizedValueMilli: number;
  readonly weightBp: number;
  readonly pointsMilli: number;
  readonly sampleCount: number;
  readonly sourceQueryRef: string | null;
  readonly explanationVi: string;
  readonly evidence: KpiEvidence;
  readonly contribution: KpiContribution;
  readonly band: KpiBand;
}

/** Shaped for a `kpi_scores` row plus its `kpi_score_details` children. */
export interface KpiScoreResult {
  readonly totalPointsMilli: number;
  readonly basePointsMilli: number;
  readonly bonusPointsMilli: number;
  /** Stored POSITIVE, matching `kpi_scores.penalty_points_milli`. */
  readonly penaltyPointsMilli: number;
  readonly normalizedPointsMilli: number;
  readonly details: readonly KpiScoreDetail[];
  /** Max rule version used — `kpi_scores.rule_version`, for reproducibility. */
  readonly ruleVersion: number;
  readonly busynessFactorBp: number;
  readonly computedAt: Millis;
  readonly engineVersion: string;
}

/**
 * Score a whole period for one employee.
 *
 * `metrics` is keyed by rule CODE, falling back to the metric name, so a caller
 * can supply one aggregate per metric and let several rules read it.
 *
 * A rule with no metric supplied is still emitted as a detail row with
 * sampleCount 0 — silence is worse than a zero the employee can see and appeal.
 *
 * BUCKETS: BASE / BONUS / PENALTY come from the rule's `contribution`, or are
 * inferred from the sign of the points. Penalties are stored positive and
 * subtracted, matching the DB comment on `kpi_scores.penalty_points_milli`.
 *
 * `normalizedPointsMilli` divides the total by the shift busyness so leaderboard
 * rank does not simply reward whoever was rostered on the busiest night.
 */
export function computeKpiScore(
  rules: readonly KpiRuleConfig[],
  metrics: Readonly<Record<string, MetricValue>>,
  context: KpiScoringContext,
): KpiScoreResult {
  const details: KpiScoreDetail[] = [];
  let base = 0;
  let bonus = 0;
  let penalty = 0;
  let ruleVersion = 0;

  for (const rule of rules) {
    const metric = metrics[rule.code] ?? metrics[rule.metric] ?? { raw: 0, sampleCount: 0 };
    const scored = scoreRule(rule, metric, context);
    const contribution = resolveContribution(rule, scored.pointsMilli);

    if (contribution === 'PENALTY') penalty += Math.abs(scored.pointsMilli);
    else if (contribution === 'BONUS') bonus += scored.pointsMilli;
    else base += scored.pointsMilli;

    if (rule.version > ruleVersion) ruleVersion = rule.version;

    details.push({
      kpiRuleId: rule.id,
      ruleCode: rule.code,
      metric: rule.metric,
      rawValueMilli: scored.evidence.rawValueMilli,
      normalizedValueMilli: scored.evidence.normalizedValueMilli,
      weightBp: rule.weightBp,
      pointsMilli: scored.pointsMilli,
      sampleCount: scored.evidence.sampleCount,
      sourceQueryRef: rule.sourceQueryRef ?? null,
      explanationVi: scored.explanationVi,
      evidence: scored.evidence,
      contribution,
      band: scored.evidence.band,
    });
  }

  const total = base + bonus - penalty;
  const busy = Math.trunc(context.busynessFactorBp);
  const normalizedPoints = busy > 0 ? divRoundHalfUp(total * BP_SCALE, busy) : total;

  return {
    totalPointsMilli: total,
    basePointsMilli: base,
    bonusPointsMilli: bonus,
    penaltyPointsMilli: penalty,
    normalizedPointsMilli: normalizedPoints,
    details,
    ruleVersion,
    busynessFactorBp: busy,
    computedAt: context.computedAt,
    engineVersion: KPI_ENGINE_VERSION,
  };
}

function resolveContribution(rule: KpiRuleConfig, pointsMilli: number): KpiContribution {
  const declared = rule.contribution;
  if (declared !== undefined) return declared;
  return pointsMilli < 0 ? 'PENALTY' : 'BASE';
}

// ---------------------------------------------------------------------------
// Anti-fraud gate
// ---------------------------------------------------------------------------

/**
 * Why an event does not count towards KPI. Written to
 * `task_events.kpi_reject_reason`, so every excluded event stays visible and
 * appealable rather than silently vanishing.
 */
export type KpiRejectReason =
  | 'NO_EMPLOYEE'
  | 'NOT_ON_SHIFT'
  | 'WRONG_BRANCH'
  | 'MISSING_PERMISSION'
  | 'MISSING_SKILL'
  | 'SKILL_LEVEL_TOO_LOW'
  | 'OUT_OF_ORDER_TRANSITION'
  | 'DUPLICATE_IDEMPOTENCY_KEY'
  | 'SCANNED_ON_BEHALF'
  | 'IMPLAUSIBLY_FAST'
  | 'ORDER_CANCELLED_ABNORMALLY'
  | 'SELF_CREATED_SELF_COMPLETED'
  | 'MISSING_EVIDENCE'
  | 'POINT_FARMING';

/** The slice of a `task_events` row the gate inspects. */
export interface TaskEventView {
  readonly taskId: string;
  readonly eventType: TaskEventType;
  readonly tenantId: string;
  readonly branchId: string;
  readonly employeeId: string | null;
  readonly stationId?: string | null;
  readonly orderId?: string | null;
  readonly idempotencyKey: string;
  readonly createdAt: Millis;
  readonly startedAt?: Millis | null;
  readonly completedAt?: Millis | null;
  readonly durationSeconds?: number | null;
  readonly source?: string | null;
  readonly evidenceFileId?: string | null;
  readonly deviceId?: string | null;
  /** Employee whose device produced the scan — must equal `employeeId`. */
  readonly scannedByEmployeeId?: string | null;
}

/** Everything the gate needs to judge the event. All loaded by the caller. */
export interface TaskEventContext {
  /** Server clock, injected. */
  readonly now: Millis;
  readonly isOnShift: boolean;
  /** Branch the employee is actually clocked in at. */
  readonly employeeBranchId: string;
  readonly employeePermissions: readonly string[];
  readonly employeeSkills: readonly { readonly code: string; readonly level: number }[];
  readonly requiredPermissionCode?: string | null;
  readonly requiredSkillCode?: string | null;
  readonly minSkillLevel?: number | null;
  /** `tasks.assigned_employee_id` at the moment of the event. */
  readonly assignedEmployeeId: string | null;
  /** Last event on this task, or null when this is the first. */
  readonly previousEventType: TaskEventType | null;
  /** Idempotency keys already stored for this tenant. */
  readonly seenIdempotencyKeys?: ReadonlySet<string>;
  /**
   * Shortest duration a human could plausibly take for this task type, in
   * seconds. From `task_definitions`, never hard-coded here.
   */
  readonly minPlausibleDurationSeconds?: number | null;
  /** True when the linked order was voided/cancelled outside the normal flow. */
  readonly orderCancelledOutsideFlow?: boolean;
  /** Who created the task (for the self-create/self-complete check). */
  readonly taskCreatedByEmployeeId?: string | null;
  /** `task_definitions.requires_evidence`. */
  readonly requiresEvidence?: boolean;
  /** How many times this employee accepted then cancelled THIS task. */
  readonly acceptCancelCount?: number;
  /** Configured tolerance before that counts as point farming. */
  readonly maxAcceptCancelCount?: number | null;
}

export interface CountabilityResult {
  readonly countable: boolean;
  readonly rejectReason?: KpiRejectReason;
}

/** Only these events can ever earn points; the rest are bookkeeping. */
const SCORING_EVENTS: ReadonlySet<TaskEventType> = new Set<TaskEventType>(['COMPLETED', 'ACCEPTED', 'STARTED', 'QR_SCANNED']);

/**
 * Legal predecessors per event type. An event whose predecessor is not listed
 * is out of order — the classic fraud pattern being COMPLETED with no STARTED,
 * i.e. someone tapping "done" on work never begun.
 */
const LEGAL_PREDECESSORS: Readonly<Record<TaskEventType, readonly (TaskEventType | null)[]>> = {
  CREATED: [null],
  OFFERED: ['CREATED', 'OFFERED', 'REJECTED', 'EXPIRED', 'REASSIGNED', 'ESCALATED'],
  ACCEPTED: ['CREATED', 'OFFERED', 'ESCALATED', 'REASSIGNED'],
  REJECTED: ['OFFERED'],
  STARTED: ['ACCEPTED'],
  PAUSED: ['STARTED', 'RESUMED'],
  RESUMED: ['PAUSED'],
  COMPLETED: ['STARTED', 'RESUMED', 'ACCEPTED', 'EVIDENCE_ADDED', 'QR_SCANNED'],
  FAILED: ['STARTED', 'RESUMED', 'ACCEPTED', 'PAUSED'],
  REASSIGNED: ['OFFERED', 'ACCEPTED', 'STARTED', 'PAUSED', 'RESUMED', 'ESCALATED', 'EXPIRED'],
  ESCALATED: ['CREATED', 'OFFERED', 'REJECTED', 'EXPIRED', 'ESCALATED'],
  CANCELLED: ['CREATED', 'OFFERED', 'ACCEPTED', 'STARTED', 'PAUSED', 'RESUMED', 'ESCALATED'],
  EXPIRED: ['CREATED', 'OFFERED'],
  EVIDENCE_ADDED: ['ACCEPTED', 'STARTED', 'PAUSED', 'RESUMED', 'COMPLETED', 'QR_SCANNED'],
  QR_SCANNED: ['CREATED', 'OFFERED', 'ACCEPTED', 'STARTED', 'RESUMED'],
};

/**
 * THE ANTI-FRAUD GATE. Decides whether a `task_events` row may earn points.
 *
 * WHY it is a separate, pure function: points farming is the failure mode that
 * kills trust in a KPI system. If any of these checks lived in an ad-hoc `if`
 * in a Worker handler, it would eventually be bypassed by a new code path.
 * Here it is one gate, unit-tested against every known cheat:
 *
 *   - clocking points while not on shift, or at another branch
 *   - doing work you are not permitted or skilled to do
 *   - "completing" a task that was never started (out-of-order transition)
 *   - replaying the same request (duplicate idempotency key)
 *   - scanning a colleague's QR on their behalf
 *   - completing in 3 seconds what takes a human 90
 *   - riding a cancelled order for points
 *   - inventing a task for yourself and closing it with no evidence
 *   - accept → cancel → accept → cancel to farm ACCEPTED events
 *
 * Rejection is NOT punishment: the event is still stored, with
 * `kpi_counted = 0` and this reason, and the employee can appeal.
 */
export function isTaskEventCountable(event: TaskEventView, context: TaskEventContext): CountabilityResult {
  // Only scoring events are gated; everything else is simply not countable.
  if (!SCORING_EVENTS.has(event.eventType)) return { countable: false };

  if (event.employeeId === null) return reject('NO_EMPLOYEE');

  // Replay first: a duplicate must never be judged on its merits.
  if (context.seenIdempotencyKeys?.has(event.idempotencyKey) === true) {
    return reject('DUPLICATE_IDEMPOTENCY_KEY');
  }

  if (!context.isOnShift) return reject('NOT_ON_SHIFT');
  if (event.branchId !== context.employeeBranchId) return reject('WRONG_BRANCH');

  // Someone scanned this on another person's behalf (or with their device).
  const scanner = event.scannedByEmployeeId ?? null;
  if (scanner !== null && scanner !== event.employeeId) return reject('SCANNED_ON_BEHALF');
  if (context.assignedEmployeeId !== null && context.assignedEmployeeId !== event.employeeId) {
    return reject('SCANNED_ON_BEHALF');
  }

  const requiredPermission = context.requiredPermissionCode ?? null;
  if (requiredPermission !== null && !context.employeePermissions.includes(requiredPermission)) {
    return reject('MISSING_PERMISSION');
  }

  const requiredSkill = context.requiredSkillCode ?? null;
  if (requiredSkill !== null) {
    const held = context.employeeSkills.find((s) => s.code === requiredSkill);
    if (held === undefined) return reject('MISSING_SKILL');
    if (held.level < (context.minSkillLevel ?? 0)) return reject('SKILL_LEVEL_TOO_LOW');
  }

  const legal = LEGAL_PREDECESSORS[event.eventType];
  if (!legal.includes(context.previousEventType)) return reject('OUT_OF_ORDER_TRANSITION');

  // Point farming: accept, cancel, accept, cancel...
  const maxAcceptCancel = context.maxAcceptCancelCount ?? null;
  if (maxAcceptCancel !== null && (context.acceptCancelCount ?? 0) > maxAcceptCancel) {
    return reject('POINT_FARMING');
  }

  if (context.orderCancelledOutsideFlow === true) return reject('ORDER_CANCELLED_ABNORMALLY');

  if (event.eventType === 'COMPLETED') {
    const minSeconds = context.minPlausibleDurationSeconds ?? null;
    if (minSeconds !== null && minSeconds > 0) {
      const actual = actualDurationSeconds(event);
      if (actual !== null && actual < minSeconds) return reject('IMPLAUSIBLY_FAST');
    }

    const creator = context.taskCreatedByEmployeeId ?? null;
    const selfMade = creator !== null && creator === event.employeeId;
    const hasEvidence = (event.evidenceFileId ?? null) !== null;
    if (selfMade && !hasEvidence) return reject('SELF_CREATED_SELF_COMPLETED');
    if (context.requiresEvidence === true && !hasEvidence) return reject('MISSING_EVIDENCE');
  }

  return { countable: true };
}

function reject(rejectReason: KpiRejectReason): CountabilityResult {
  return { countable: false, rejectReason };
}

/**
 * Elapsed work time. Prefers the explicit `durationSeconds`, else derives it
 * from server timestamps. Device clocks are never used — a phone whose clock
 * was moved back could otherwise manufacture a plausible duration.
 */
function actualDurationSeconds(event: TaskEventView): number | null {
  const explicit = event.durationSeconds ?? null;
  if (explicit !== null && explicit >= 0) return Math.trunc(explicit);
  const startedAt = event.startedAt ?? null;
  const completedAt = event.completedAt ?? event.createdAt;
  if (startedAt === null) return null;
  return Math.max(0, Math.floor((completedAt - startedAt) / 1000));
}

// ---------------------------------------------------------------------------
// Seed rules
// ---------------------------------------------------------------------------

/** Common shape helper so the seed table below stays readable. */
function seedRule(
  code: string,
  roleCode: string,
  nameVi: string,
  metric: KpiMetric,
  aggregation: KpiAggregation,
  weightBp: number,
  pointsPerUnitMilli: number,
  options: {
    readonly normalization?: KpiNormalization;
    readonly targetValueMilli?: number | null;
    readonly minPointsMilli?: number | null;
    readonly maxPointsMilli?: number | null;
    readonly thresholds?: KpiThresholds;
    readonly contribution?: KpiContribution;
  } = {},
): KpiRuleConfig {
  return {
    id: `KPI_SEED_${code}`,
    code,
    nameVi,
    metric,
    aggregation,
    formula: null,
    weightBp,
    pointsPerUnitMilli,
    minPointsMilli: options.minPointsMilli ?? null,
    maxPointsMilli: options.maxPointsMilli ?? null,
    targetValueMilli: options.targetValueMilli ?? null,
    normalization: options.normalization ?? 'NONE',
    thresholds: options.thresholds ?? {},
    version: 1,
    contribution: options.contribution ?? 'BASE',
    roleCode,
    taskType: null,
    sourceQueryRef: `agg:${metric.toLowerCase()}`,
  };
}

/** Bands used by most rate rules: rates are milli-fractions (1000 = 100%). */
const RATE_BANDS_HIGH_GOOD: KpiThresholds = {
  green: { gte: 900 },
  amber: { gte: 750, lt: 900 },
  red: { lt: 750 },
};
const RATE_BANDS_LOW_GOOD: KpiThresholds = {
  green: { lte: 30 },
  amber: { gt: 30, lte: 80 },
  red: { gt: 80 },
};

/**
 * SEED DATA — a sensible starting rule set per role.
 *
 * IMPORTANT: this is NOT the engine's logic. These rows are inserted into
 * `kpi_rules` by the seeder; after that every tenant edits them from the Admin
 * app, and the engine only ever reads what the database holds. Nothing here is
 * consulted at scoring time.
 *
 * Design notes:
 *  - Volume metrics use PER_SHIFT_BUSYNESS so a quiet shift is not punished.
 *  - Quality rates use NONE: a rate is already comparable.
 *  - Speed metrics use PERCENT_OF_TARGET with a NEGATIVE points-per-unit, so
 *    being slower than target costs points and being faster earns them.
 *  - Every rule is clamped, so a single mis-typed coefficient cannot wreck a
 *    payroll run.
 */
export const DEFAULT_KPI_RULES: readonly KpiRuleConfig[] = [
  // --- SERVER (phục vụ) ---------------------------------------------------
  seedRule('SERVER_TASK_COUNT', 'SERVER', 'Số nhiệm vụ hoàn thành', 'TASK_COUNT', 'COUNT', 3000, 1000, {
    normalization: 'PER_SHIFT_BUSYNESS',
    targetValueMilli: 35_000,
    maxPointsMilli: 60_000,
  }),
  seedRule('SERVER_ON_TIME_RATE', 'SERVER', 'Tỉ lệ phục vụ đúng hạn', 'ON_TIME_RATE', 'RATIO', 3000, 40_000, {
    targetValueMilli: 900,
    maxPointsMilli: 40_000,
    thresholds: RATE_BANDS_HIGH_GOOD,
  }),
  seedRule('SERVER_TABLES_SERVED', 'SERVER', 'Số bàn phục vụ', 'TABLES_SERVED', 'COUNT', 1500, 800, {
    normalization: 'PER_SHIFT_BUSYNESS',
    maxPointsMilli: 30_000,
  }),
  seedRule('SERVER_CUSTOMER_RATING', 'SERVER', 'Đánh giá của khách', 'CUSTOMER_RATING', 'AVG', 2000, 6000, {
    targetValueMilli: 4500,
    maxPointsMilli: 30_000,
  }),
  seedRule('SERVER_ABANDON_RATE', 'SERVER', 'Tỉ lệ bỏ dở nhiệm vụ', 'ABANDON_RATE', 'RATIO', 10_000, -60_000, {
    minPointsMilli: -30_000,
    maxPointsMilli: 0,
    contribution: 'PENALTY',
    thresholds: RATE_BANDS_LOW_GOOD,
  }),

  // --- KITCHEN (bếp) ------------------------------------------------------
  seedRule('KITCHEN_ITEMS_SERVED', 'KITCHEN', 'Số món đã ra', 'ITEMS_SERVED', 'COUNT', 3500, 600, {
    normalization: 'PER_SHIFT_BUSYNESS',
    targetValueMilli: 120_000,
    maxPointsMilli: 80_000,
  }),
  seedRule('KITCHEN_ON_TIME_RATE', 'KITCHEN', 'Tỉ lệ ra món đúng hạn', 'ON_TIME_RATE', 'RATIO', 3500, 45_000, {
    targetValueMilli: 900,
    maxPointsMilli: 45_000,
    thresholds: RATE_BANDS_HIGH_GOOD,
  }),
  seedRule('KITCHEN_AVG_DURATION', 'KITCHEN', 'Thời gian chế biến trung bình', 'AVG_DURATION_SECONDS', 'AVG', 1500, -2, {
    normalization: 'PERCENT_OF_TARGET',
    targetValueMilli: 480_000,
    minPointsMilli: -20_000,
    maxPointsMilli: 20_000,
  }),
  seedRule('KITCHEN_REMAKE_RATE', 'KITCHEN', 'Tỉ lệ làm lại món', 'REMAKE_RATE', 'RATIO', 10_000, -80_000, {
    minPointsMilli: -40_000,
    maxPointsMilli: 0,
    contribution: 'PENALTY',
    thresholds: RATE_BANDS_LOW_GOOD,
  }),

  // --- BAR (pha chế) ------------------------------------------------------
  seedRule('BAR_ITEMS_SERVED', 'BAR', 'Số đồ uống đã pha', 'ITEMS_SERVED', 'COUNT', 4000, 700, {
    normalization: 'PER_SHIFT_BUSYNESS',
    targetValueMilli: 100_000,
    maxPointsMilli: 70_000,
  }),
  seedRule('BAR_ON_TIME_RATE', 'BAR', 'Tỉ lệ pha chế đúng hạn', 'ON_TIME_RATE', 'RATIO', 4000, 45_000, {
    targetValueMilli: 920,
    maxPointsMilli: 45_000,
    thresholds: RATE_BANDS_HIGH_GOOD,
  }),
  seedRule('BAR_REMAKE_RATE', 'BAR', 'Tỉ lệ pha lại', 'REMAKE_RATE', 'RATIO', 10_000, -80_000, {
    minPointsMilli: -40_000,
    maxPointsMilli: 0,
    contribution: 'PENALTY',
    thresholds: RATE_BANDS_LOW_GOOD,
  }),

  // --- CASHIER (thu ngân) -------------------------------------------------
  seedRule('CASHIER_TASK_COUNT', 'CASHIER', 'Số giao dịch xử lý', 'TASK_COUNT', 'COUNT', 3000, 500, {
    normalization: 'PER_SHIFT_BUSYNESS',
    maxPointsMilli: 60_000,
  }),
  seedRule(
    'CASHIER_AVG_RESPONSE',
    'CASHIER',
    'Thời gian phản hồi khách trung bình',
    'AVG_RESPONSE_SECONDS',
    'AVG',
    2000,
    -2,
    {
      normalization: 'PERCENT_OF_TARGET',
      targetValueMilli: 60_000,
      minPointsMilli: -20_000,
      maxPointsMilli: 20_000,
    },
  ),
  seedRule('CASHIER_ON_TIME_RATE', 'CASHIER', 'Tỉ lệ thanh toán đúng hạn', 'ON_TIME_RATE', 'RATIO', 3000, 40_000, {
    targetValueMilli: 950,
    maxPointsMilli: 40_000,
    thresholds: RATE_BANDS_HIGH_GOOD,
  }),
  seedRule('CASHIER_CUSTOMER_RATING', 'CASHIER', 'Đánh giá của khách', 'CUSTOMER_RATING', 'AVG', 2000, 6000, {
    targetValueMilli: 4500,
    maxPointsMilli: 30_000,
  }),

  // --- WAREHOUSE (kho) ----------------------------------------------------
  seedRule('WAREHOUSE_TASK_COUNT', 'WAREHOUSE', 'Số phiếu kho xử lý', 'TASK_COUNT', 'COUNT', 4000, 1500, {
    normalization: 'PER_HOUR',
    maxPointsMilli: 60_000,
  }),
  seedRule('WAREHOUSE_COMPLETION_RATE', 'WAREHOUSE', 'Tỉ lệ hoàn thành phiếu', 'COMPLETION_RATE', 'RATIO', 3000, 45_000, {
    targetValueMilli: 980,
    maxPointsMilli: 45_000,
    thresholds: RATE_BANDS_HIGH_GOOD,
  }),
  seedRule('WAREHOUSE_ON_TIME_RATE', 'WAREHOUSE', 'Tỉ lệ nhập/xuất đúng hạn', 'ON_TIME_RATE', 'RATIO', 3000, 40_000, {
    targetValueMilli: 950,
    maxPointsMilli: 40_000,
    thresholds: RATE_BANDS_HIGH_GOOD,
  }),

  // --- ALL ROLES: attendance discipline -----------------------------------
  seedRule('ALL_LATE_MINUTES', 'ALL', 'Số phút đi muộn', 'LATE_MINUTES', 'SUM', 10_000, -500, {
    minPointsMilli: -30_000,
    maxPointsMilli: 0,
    contribution: 'PENALTY',
  }),
];
